import { FC } from 'react';
import { MenuSubItemInterface } from '../../types';
import { Link } from 'react-router-dom';
import { Text } from 'stubben-edge-unified-frontend-react-components';
import { useStore } from '@/hooks/useStore';
import { navLinkToTestSegment, navTitleToTestSegment } from '../../navTestId';
import useFavourites from '@/hooks/useFavourites';
import FavouriteToggle from '@/components/common/FavouriteToggle';
import { createFavouriteItem } from '@/utils/favouritesStorage';

const MenuSubItem: FC<MenuSubItemInterface> = ({ title, link, isExternal }) => {
  const { interfaceStore } = useStore();
  const { isFavourite, toggleFavourite } = useFavourites();
  const isActive = window.location.pathname + window.location.search === link;

  const canFavourite =
    !!link && !isExternal && typeof title === 'string' && title !== 'Log out';
  const favouriteId = canFavourite ? `nav:${link}` : null;
  const favourited = favouriteId ? isFavourite(favouriteId) : false;

  const label = (
    <Text
      size="sm"
      variant={isActive ? 'primary' : 'quaternary'}
      mode={isActive ? 'brand' : 'normal'}
      bold={isActive ? 'medium' : 'regular'}
      testIdentifier={
        link
          ? `sidebar-nav-sublink-label-${navLinkToTestSegment(link as string)}`
          : `sidebar-nav-sublink-label-${navTitleToTestSegment(title)}`
      }
    >
      {title}
    </Text>
  );

  const favouriteToggle =
    canFavourite && favouriteId ? (
      <div className="pr-1">
        <FavouriteToggle
          isFavourited={favourited}
          hoverReveal={!favourited}
          size={16}
          testIdentifier={`sidebar-nav-favourite-${navLinkToTestSegment(
            link as string
          )}`}
          onToggle={() =>
            toggleFavourite(
              createFavouriteItem({
                type: 'nav',
                stableKey: link as string,
                title: title as string,
                path: link as string
              })
            )
          }
        />
      </div>
    ) : null;

  const wrapperClassName = `group mx-2.5 flex items-center gap-1 rounded hover:bg-gray-50 ${
    isActive ? 'bg-primary-50 font-semibold text-primary' : 'text-gray-500'
  }`;
  const innerClassName = 'min-w-0 flex-1 cursor-pointer px-3 py-2 text-left';

  if (link && isExternal) {
    return (
      <div className={wrapperClassName}>
        <a
          href={link as string}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`sidebar-nav-${navLinkToTestSegment(link as string)}`}
          className={innerClassName}
        >
          {label}
        </a>
        {favouriteToggle}
      </div>
    );
  }

  if (link) {
    return (
      <div className={wrapperClassName}>
        <Link
          to={link as string}
          data-testid={`sidebar-nav-${navLinkToTestSegment(link as string)}`}
          onClick={() => interfaceStore.toggleMenuDrawer(false)}
          className={innerClassName}
        >
          {label}
        </Link>
        {favouriteToggle}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <button
        type="button"
        data-testid={`sidebar-nav-sublink-${navTitleToTestSegment(title)}`}
        className={innerClassName}
      >
        {label}
      </button>
    </div>
  );
};
export default MenuSubItem;
